/**
 * Client-side PDF for flight confirmation (Duffel order summary).
 * Uses dynamic import so jspdf loads only when the user downloads.
 */

import { pdfSafeText } from './pdfSafeText';

function fmtDt(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function legDur(dep, arr) {
  if (!dep || !arr) return '';
  const ms = new Date(arr) - new Date(dep);
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

/**
 * @param {{ detail: object, primaryReference?: string, orderId?: string }} params
 */
export async function downloadFlightConfirmationPdf({ detail, primaryReference, orderId }) {
  if (!detail) return;

  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 14;
  // Fixed wrap width (mm): deriving from pageSize can be wrong in some browsers -> char-split garbage.
  const maxW = 182;
  let y = 18;
  const lh = 5.5;
  const secGap = 4;

  const ensure = (need = lh) => {
    if (y + need > 288) {
      doc.addPage();
      y = 16;
    }
  };

  const writeHeading = (text) => {
    ensure(secGap + 8);
    y += secGap;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(pdfSafeText(text), margin, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
  };

  const writeLines = (text) => {
    const raw = pdfSafeText(String(text ?? '').replace(/\r\n/g, '\n'));
    if (!raw) return;
    raw.split('\n').forEach((para) => {
      const block = para.trim();
      if (!block) return;
      const wrapped = doc.splitTextToSize(block, maxW);
      const lines = Array.isArray(wrapped) ? wrapped : [String(wrapped)];
      lines.forEach((ln) => {
        const line = typeof ln === 'string' ? ln : String(ln);
        if (!line) return;
        ensure();
        doc.text(line, margin, y);
        y += lh;
      });
    });
  };

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(pdfSafeText('Tourasco - Flight confirmation'), margin, y);
  y += 9;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  writeLines(`Generated: ${new Date().toLocaleString()}`);
  if (orderId) writeLines(`Order ID: ${orderId}`);
  y += 2;

  writeHeading('Booking references');
  if (primaryReference) writeLines(`Booking reference: ${primaryReference}`);
  if (detail.validatingCarrier) writeLines(`Validating carrier: ${detail.validatingCarrier}`);
  if (detail.airlineRefs.length === 0 && !primaryReference) {
    writeLines('Airline references: see your confirmation email if not listed here.');
  } else {
    detail.airlineRefs.forEach((r) => writeLines(`${r.label}: ${r.reference}`));
  }

  writeHeading('Ticket numbers');
  if (detail.ticketDocuments.length === 0) {
    writeLines(
      'No ticket documents were listed on this order yet - check your airline confirmation email.',
    );
  } else {
    detail.ticketDocuments.forEach((t, i) => {
      const lines = [];
      if (t.displayLabel) lines.push(t.displayLabel);
      else if (t.ticketNumber) lines.push(`Ticket / reference: ${t.ticketNumber}`);
      else lines.push(`Ticket ${i + 1}`);
      if (t.documentId) lines.push(`Document ID: ${t.documentId}`);
      if (t.passengerLabel) lines.push(`Passenger: ${t.passengerLabel}`);
      lines.push(`Type: ${t.type || 'electronic ticket'}`);
      writeLines(lines.filter(Boolean).join('\n'));
    });
  }

  writeHeading('Itinerary');
  if (detail.itinerary.length === 0) {
    writeLines('Full schedule: see your airline confirmation email.');
  } else {
    detail.itinerary.forEach((leg, idx) => {
      writeLines(
        `${idx + 1}. ${leg.sliceLabel} - ${leg.flightNum}${leg.carrier ? ` * ${leg.carrier}` : ''}`,
      );
      writeLines(
        `   ${leg.originCode} ${fmtTime(leg.depart)} -> ${leg.destCode} ${fmtTime(leg.arrive)} (${legDur(leg.depart, leg.arrive)})`,
      );
      writeLines(`   Depart: ${fmtDt(leg.depart)}   Arrive: ${fmtDt(leg.arrive)}`);
      if (leg.originName || leg.destName) {
        writeLines(`   ${leg.originName || ''} -> ${leg.destName || ''}`);
      }
      y += 2;
    });
  }

  writeHeading('Travelers');
  if (detail.passengers.length === 0) {
    writeLines('See airline confirmation for traveler names.');
  } else {
    detail.passengers.forEach((p) => {
      let line = p.displayName;
      if (p.passengerType) line += ` - ${p.passengerType}`;
      writeLines(line);
      if (p.email) writeLines(`   Email: ${p.email}`);
    });
  }

  writeHeading('Payment');
  if (detail.totalAmount != null && detail.totalCurrency) {
    writeLines(`Total: ${detail.totalCurrency} ${detail.totalAmount}`);
  }
  writeLines('Paid via Stripe. Ticket issued through Duffel to the airline.');

  writeHeading('Before you fly');
  [
    'Bring the same ID or passport as used for booking.',
    'Check in online with the airline using your airline reference when available.',
    'Arrive at the airport with enough time for security.',
    'Keep this PDF with you when you travel.',
  ].forEach((t) => writeLines(`- ${t}`));

  const rawSlug = primaryReference || orderId || 'confirmation';
  const slug = String(rawSlug)
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .slice(0, 48);
  doc.save(`tourasco-flight-${slug}.pdf`);
}
